import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './en.json';

export type PortalLocale = 'en' | 'kn';

const STORAGE_KEY = 'portal-locale';

type Messages = Record<string, unknown>;

/**
 * `en` ships in the entry bundle because it is both the default locale and the
 * fallback for any key missing from another locale — `t()` must be able to
 * resolve it synchronously on the very first render.
 *
 * `kn` (40 KB) is fetched on demand. It used to be a static import, which meant
 * every visitor to the marketing site and every staff user downloaded a Kannada
 * translation table they would never read.
 */
const LOADED: Partial<Record<PortalLocale, Messages>> = { en: en as Messages };

const LOADERS: Record<PortalLocale, () => Promise<Messages>> = {
  en: async () => en as Messages,
  kn: () => import('./kn.json').then((m) => m.default as Messages),
};

function readStoredLocale(): PortalLocale {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'kn' || v === 'en') return v;
  } catch {
    /* noop */
  }
  return 'en';
}

function getNested(obj: Messages, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

function interpolate(template: string, vars?: Record<string, string>): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, k: string) => vars[k] ?? '');
}

type PortalLocaleContextValue = {
  locale: PortalLocale;
  setLocale: (locale: PortalLocale) => void;
  t: (key: string, vars?: Record<string, string>) => string;
};

const PortalLocaleContext = createContext<PortalLocaleContextValue | null>(null);

export function PortalLocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<PortalLocale>(() => readStoredLocale());
  // Bumped when an on-demand locale finishes loading, so `t` re-runs with it.
  const [loadedTick, setLoadedTick] = useState(0);

  const ensureLoaded = useCallback((next: PortalLocale) => {
    if (LOADED[next]) return;
    void LOADERS[next]()
      .then((msgs) => {
        LOADED[next] = msgs;
        setLoadedTick((n) => n + 1);
      })
      .catch(() => {
        /* fall back to en — t() already does this per key */
      });
  }, []);

  // Load the stored locale on mount; a returning Kannada user briefly sees
  // English rather than a blank screen, which is the better failure mode.
  useEffect(() => {
    ensureLoaded(locale);
  }, [locale, ensureLoaded]);

  const setLocale = useCallback(
    (next: PortalLocale) => {
      ensureLoaded(next);
      setLocaleState(next);
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* noop */
      }
    },
    [ensureLoaded],
  );

  const t = useCallback(
    (key: string, vars?: Record<string, string>) => {
      const active = LOADED[locale];
      if (active) {
        const raw = getNested(active, key);
        if (typeof raw === 'string') return interpolate(raw, vars);
      }
      const fallback = getNested(LOADED.en as Messages, key);
      if (typeof fallback === 'string') return interpolate(fallback, vars);
      return key;
    },
    // loadedTick is a render trigger: `t` must be a new identity once the
    // on-demand table lands, or memoised consumers keep the English strings.
    [locale, loadedTick],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <PortalLocaleContext.Provider value={value}>{children}</PortalLocaleContext.Provider>;
}

export function usePortalT() {
  const ctx = useContext(PortalLocaleContext);
  if (!ctx) throw new Error('usePortalT must be used within PortalLocaleProvider');
  return ctx;
}

export function PortalLanguageSwitcher({
  className = '',
  variant = 'dark',
}: {
  className?: string;
  variant?: 'dark' | 'light';
}) {
  const { locale, setLocale } = usePortalT();
  const border = variant === 'dark' ? 'border-white/20' : 'border-gray-200';
  const active = variant === 'dark' ? 'bg-white text-[#09175d]' : 'bg-[#09175d] text-white';
  const idle = variant === 'dark' ? 'text-white/80 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100';
  return (
    <div className={`inline-flex rounded-lg border overflow-hidden text-xs font-semibold ${border} ${className}`}>
      <button
        type="button"
        onClick={() => setLocale('en')}
        className={`px-2.5 py-1 ${locale === 'en' ? active : idle}`}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => setLocale('kn')}
        className={`px-2.5 py-1 ${locale === 'kn' ? active : idle}`}
      >
        ಕನ್ನಡ
      </button>
    </div>
  );
}

/** Map CRM pipeline stage name → i18n key suffix under applications.status */
export function portalStatusKeyFromCrmStage(stageName: string | null | undefined): string {
  const n = (stageName ?? '').trim().toLowerCase();
  if (n === 'new') return 'applications.status.new';
  if (n === 'contacted') return 'applications.status.contacted';
  if (n === 'proposal sent') return 'applications.status.proposalSent';
  if (n === 'negotiation') return 'applications.status.negotiation';
  if (n === 'won') return 'applications.status.intake';
  if (n === 'lost') return 'applications.status.dropped';
  return 'applications.status.unknown';
}

export function portalContactPhone(): string {
  return (import.meta.env.VITE_PORTAL_CONTACT_PHONE as string | undefined)?.trim() ?? '';
}
